import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../context/Translation/TranslationHooks';
import { useAppCompanyState, useAppGlobalState, useAppSiteState, useVrsTranslationState } from '../../../context/AppContext/AppContext';
import ActionTypes from '../base/ActionTypes';
import { IAction } from '../interfaces/IAction';

import { MemoryStoreLibrary } from '../utilities/MemoryStoreLibrary';
import { DesignConstants } from '../utilities/DesignConstants';
import {
  mergeObjects,
  encodeUTF8,
  resolveAbsolutePath,
  getFirstArrayElementOrNull,
  base64ResponseToImageInfoAsHMM,
  checkObjectExistsInArray,
  createObservable,
} from '../utilities/UtilityLibrary';
import { useConfigActions } from '../../../actions/configActions';
import { useDispatch } from '../context/Store/StoreHooks';
import { setSelectedTabIndexInvoker } from '../utilities/StoreLibrary';
import { useToaster } from '../../../context/ToasterContext/ToasterContext';
import { IParserData, useDialogLanguageParser } from '../utilities/DialogLanguageParser';
import { DialogProps } from '@mui/material';
import { IDraggableBlock } from '../interfaces/IDraggableBlock';
import { DesignDialogActionsContext } from './DesignDialogActionsContext';
import { IDesignDialogActions } from './IDesignDialogActions';

export const DesignDialogActionsProvider: React.FC<any> = ({ children, ...props }) => {
  // The provider calls the hook with the props it receives.
  // When these props change, the hook re-runs, and `actions` gets updated.
  const { _T } = useVrsTranslationState();
  const toaster = useToaster();
  const { getTranslatedString } = useTranslation();
  const dispatch = useDispatch();

  const { isPureDesignUser } = useAppGlobalState();
  const { selectedSiteId } = useAppSiteState();

  // Dialog Core States
  const [dialogProps, setDialogProps] = useState<any>({});
  const [pageTitle, setPageTitle] = useState<string>('');
  const [dialogOpenIndex, setDialogOpenIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogName, setDialogName] = useState<string>('emptydialog');
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [baseUrl, setBaseUrl] = useState('');
  const [maxWidth] = useState<DialogProps["maxWidth"]>("md");

  // Dialog Lifecycle States
  const [initateDisplayComponents, setInitateDisplayComponents] = useState(false);
  const [isDialogClosing, setIsDialogClosing] = useState(false);
  const [dialogInitialised, setDialogInitialised] = useState(true);
  const [pageRefreshed, setPageRefreshed] = useState(false);
  const [previousActionFromParent, setPreviousActionFromParent] = useState<any>(null);

  // Data Management States
  const [PropertyCollectionDictionary, setPropertyCollectionDictionary] = useState({});
  const [everyRequestPostData, setEveryRequestPostData] = useState<any>({});
  const [firstRequestPostData, setFirstRequestPostData] = useState({});
  const [fieldNameShadowCopy, setFieldNameShadowCopy] = useState('');
  const [parsedDialogData, setParsedDialogData] = useState<IParserData>({
    displayableControls: [],
    nonDisplayableControls: [],
    tabDefinitions: [],
    nonComponentData: {},
    controlsWithParents: [],
  });

  // UI Control States
  const [tabBarVisible, setTabBarVisible] = useState(false);
  const [okButtonDefinition, setOkButtonDefinition] = useState<any>(null);
  const [closeButtonDefinition, setCloseButtonDefinition] = useState<any>(null);
  const [selectedTabBarIconIndex, setSelectedTabBarIconIndex] = useState(0);
  const [subAddFieldDialogOpened, setSubAddFieldDialogOpened] = useState(false);

  // Validation and Error States
  const [controlErrorFlags, setControlErrorFlags] = useState({});
  const [isDialogInError, setIsDialogInError] = useState(false);

  // Dialog Type Specific States
  const [nonEmptyGroupName, setNonEmptyGroupName] = useState('');
  const [dateOrTimeDialog, setDateOrTimeDialog] = useState(false);
  const [dialogSingleStemActionType, setDialogSingleStemActionType] = useState<any>("");

  // Preview Related States
  const [previewAvailable, setPreviewAvailable] = useState(true);
  const [previewImageUrl, setPreviewImageUrl] = useState<any>(null);
  const [previewWorkflowIndex, setPreviewWorkflowIndex] = useState(0);

  // Block Management States
  const [aiBlocksReordered, setAiBlocksReordered] = useState(false);
  const [previousSelectedBlocks, setPreviousSelectedBlocks] = useState<any>([]);
  const [selectedBlocks, setSelectedBlocks] = useState(createObservable<Array<IDraggableBlock>>([]));

  // User Input States
  const [isUserEditable, setIsUserEditable] = useState(false);
  const [isFixedLength, setIsFixedLength] = useState(false);

  // Warning/Message States
  const [defaultUserLevelWarningTitle] = useState(getTranslatedString("defaultUserLevelWarningTitle"));
  const [defaultUserLevelWarningMessage] = useState(getTranslatedString("CE_CirrusUnsupportedFeature"));

  // Refs

  const stackedDialogs = useRef<any[]>([]);

  // Computed States
  const lotCodeDialog = useMemo(() => dialogName === 'lotcodemanager', [dialogName]);
  const mergeFieldDialog = useMemo(() => dialogName === 'mergefieldmanager', [dialogName]);
  const dialogTabBarProps = useMemo(() => ({
    componentType: 'dialogtabbar',
    showPreviewButton: false
  }), []);

  const previewAvailableInDialog = useMemo(() =>
    dialogName === 'compositedatabuildermanager' ||
    dateOrTimeDialog ||
    lotCodeDialog ||
    mergeFieldDialog,
    [dialogName, dateOrTimeDialog, lotCodeDialog, mergeFieldDialog]
  );

  // Helper functions from dialogProps
  const {
    extraHandlerOnCancel,
    extraHandlerOnSave
  } = dialogProps;

  // Update Functions
  const updatePropertyCollectionDictionary = useCallback((collectionObj: any) => {
    setPropertyCollectionDictionary((prev) => ({
      ...prev,
      ...collectionObj,
    }));
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogOpenIndex(0);
    setPageTitle("");
    setParsedDialogData({
      displayableControls: [],
      nonDisplayableControls: [],
      tabDefinitions: [],
      nonComponentData: {},
      controlsWithParents: [],
    });
    setTabBarVisible(false);
    setPreviewImageUrl(null);
    setDialogName("emptydialog");
  }, []);


  const openDialog = useCallback((pageOpenData: any) => {
    dispatch({ type: ActionTypes.STORE_UpdateDoesDialogHaveErrorAction, payload: false });
    setInitateDisplayComponents(false);
    setParsedDialogData({
      displayableControls: [],
      nonDisplayableControls: [],
      tabDefinitions: [],
      nonComponentData: {},
      controlsWithParents: [],
    });
    let pageName = pageOpenData.controlPageName;
    const genericPages = ['addFieldWizard', 'editFieldWizard', 'editjob', 'printHeadAssignment'];
    if (genericPages.indexOf(pageName) > -1) {
      pageName = 'generic';
    }

    setFirstRequestPostData(pageOpenData.firstTimePostData);
    setEveryRequestPostData(pageOpenData.postData);
    setDialogSingleStemActionType(pageOpenData.dialogSingleStemActionType);

    setDialogProps({
      ...pageOpenData,
      approveDisabled: pageOpenData.approveDisabled,
      dialogName: pageName,
    });

    setBaseUrl(pageOpenData.initialDataUrl);
    setDialogOpenIndex(1);
    setDialogName(pageName);
  }, [dispatch]);

  const checkPreviewAvailable = useCallback((pageData: any) => {
    const stepActionType = pageData.SingleStemActionType || dialogSingleStemActionType;
    if (dialogName === "genericDialog") {
      // Do nothing
    } else if (dialogName === "mergefieldmanager" || dialogName !== 'compositedatabuildermanager') {
      setPreviewAvailable(stepActionType === 'MergeProps');
    }
  }, [dialogName, dialogSingleStemActionType]);


  const configActions = useConfigActions();
  const { selectedCompanyId, selectedSiteIdForCompany } = useAppCompanyState();


  const printerType = useMemo(() => {
    return (window as any).MemorySessionStorage["printerType"];
  }, []);

  const printerFormat = useMemo(() => {
    return (window as any).MemorySessionStorage["printerFormat"];
  }, []);

  const {

    // Actions from useSimpleDialogActions
    runSimpleAjaxCommandAsync,
    sendActionToParent,
    changeBlockDialogStatus,
    sendActionToChildren,
    dialogStateToPropsObservable

  } = props;

  // Basic Actions
  const updateDialogError = useCallback((hasError: boolean) => {
    dispatch({ type: ActionTypes.STORE_UpdateDoesDialogHaveErrorAction, payload: hasError });
  }, [dispatch]);

  const updateDialogBlocked = useCallback((isBlocked: boolean) => {
    dispatch({ type: ActionTypes.STORE_UpdateIsDialogBlockedAction, payload: isBlocked });
  }, [dispatch]);

  // Keep only these essential hooks and selectors
  const { parseDialogData } = useDialogLanguageParser(DesignConstants.parserTypePageControl);



  const performErrorAction = useCallback((action: IAction) => {
    sendActionToParent({
      type: ActionTypes.EXECUTE_ShowAlertBoxAction,
      payload: action.payload,
    });
    if (action.payload.closeDialog) {
      closeDialog();
    }
  }, [closeDialog, sendActionToParent]);

  const resolveTheSameLevelActions = useCallback((action: IAction) => {
    if (action && action.type) {
      switch (action.type) {
        case ActionTypes.UP_UpdateIsBlockedAction:
          dispatch({ type: ActionTypes.STORE_UpdateIsDialogBlockedAction, payload: action.payload });
          break;

        case ActionTypes.UP_UpdateShowErrorStatusAction:
          dispatch({ type: ActionTypes.STORE_UpdateDoesDialogHaveErrorAction, payload: action.payload });
          sendActionToChildren({
            type: ActionTypes.DOWN_UpdateDialogTabShowErrorStatusAction,
            payload: action.payload,
          });
          break;
        case ActionTypes.UP_AddNewBlockToBlockListAction:
          sendActionToChildren(action);
          break;

        case ActionTypes.UP_UpdateIsOpenAction:
          setIsDialogOpen(action.payload);
          break;

        case ActionTypes.UP_InitiateOpenDialogAction:
          openDialog(action.payload);
          break;

        case ActionTypes.UP_InitiateReOpenDialogAction:
          openDialog(action.payload);
          break;

        case ActionTypes.UP_CloseDialogAction:
          closeDialog();
          break;

        case ActionTypes.EXECUTE_ShowAlertBoxAction:
        case ActionTypes.EXECUTE_ShowInputBoxAction:
        case ActionTypes.EXECUTE_ShowUserLevelWarningDialogAction:
        case ActionTypes.EXECUTE_ShowDecisionDialogAction:
        case ActionTypes.UP_CiffEditorRefreshFieldImageAction:
        case ActionTypes.UP_ReloadPropertyGridForNamedFieldsAction:
        case ActionTypes.UP_RefreshJobTierFilterListAndUpdateListAction:
        case ActionTypes.UP_UpdateUndoRedoButtonsAction:
        case ActionTypes.UP_FileIconEnableAction:
        case ActionTypes.UP_ShowErrorEndReturnToMainPageAction:
        case ActionTypes.UP_ShowErrorAndRefreshPageAction:
        case ActionTypes.UP_RefreshDrawFilePanelAction:
        case ActionTypes.UP_RunProductCommandAction:
        case ActionTypes.UP_GridPanelSetFileDataAction:
        case ActionTypes.UP_CiffEditorAddNewFieldToEditorCompletedAction:
          sendActionToParent(action);
          break;


        case ActionTypes.UP_ChildDialogHasBeenInitialisedAction:
          setIsDialogVisible(true);
          break;

        case ActionTypes.UP_ShowErrorAction:
          performErrorAction(action);
          break;

        case ActionTypes.UP_Reset_Ai_Properties_Form:
          sendActionToChildren({
            type: ActionTypes.DOWN_Reset_Ai_Properties_Form,
            payload: action.payload
          });
          break;

        case ActionTypes.UP_ExecuteButtonLinkAction:
        case ActionTypes.UP_PageControlValidationStateAction:
          sendActionToChildren(action);
          break;
      }
    }
  }, [dispatch, sendActionToChildren, openDialog, closeDialog, sendActionToParent, performErrorAction]);


  const designDialogProcessComponentsData = useCallback((pageData) => {

    if (pageData.SingleStemActionType) {
      setDialogSingleStemActionType(pageData.SingleStemActionType);
    }

    checkPreviewAvailable(pageData);

    if (pageData.BaseURL) {
      setBaseUrl(pageData.BaseURL);
    }

    setDateOrTimeDialog(pageData.Name === "DateOffsetSection" || pageData.Name === "DateFormatManager");

    let emptyGroupName = "";

    if (pageData.Groups) {
      const nonEmptyGroups = pageData.Groups.filter((group) => group.Properties.length > 0);
      if (nonEmptyGroups.length > 0) {
        emptyGroupName = nonEmptyGroups[0].Name;
      }
    }

    setNonEmptyGroupName(emptyGroupName);
    setDialogInitialised(false);
    setPreviewImageUrl("");
    changeBlockDialogStatus(false);

    const parsedData = parseDialogData(pageData);

    //Inject model to tab buttons
    for (const control of parsedData.tabDefinitions) {
      control.mapStateToProps = dialogStateToPropsObservable.current;
      if ((selectedTabBarIconIndex && parsedData.tabDefinitions.indexOf(control) === selectedTabBarIconIndex)
        || (!selectedTabBarIconIndex && parsedData.tabDefinitions.indexOf(control) === 0)) {
        control.selected = true;
      } else {
        control.selected = false;
      }
    }

    const tempErrors = {};

    for (const control of parsedData.displayableControls) {
      // Controls are in a valid state unless we're notified otherwise
      tempErrors[control.PropertyName] = DesignConstants.controlStateValid;
    }

    setControlErrorFlags(s => ({ ...s, ...tempErrors }));



    // Now look through our non display components and act on the instructions they provide

    // Enable/Disable close button at dialog top
    if (checkObjectExistsInArray(parsedData.nonDisplayableControls, { PropertyName: "CloseButton" })) {
      setCloseButtonDefinition(getFirstArrayElementOrNull(parsedData.nonDisplayableControls, { PropertyName: "CloseButton" }));
      resolveTheSameLevelActions({
        type: ActionTypes.UP_UpdateShowCloseButtonAction,
        payload: true
      });
    } else {
      setCloseButtonDefinition(null);
      resolveTheSameLevelActions({
        type: ActionTypes.UP_UpdateShowCloseButtonAction,
        payload: false
      });
    }


    if (checkObjectExistsInArray(parsedData.nonDisplayableControls, { PropertyName: "OkButton" })) {
      setOkButtonDefinition(getFirstArrayElementOrNull(parsedData.nonDisplayableControls, { PropertyName: "OkButton" }));
    } else {
      setOkButtonDefinition(null);
    }

    // Dialog title

    if (checkObjectExistsInArray(parsedData.nonDisplayableControls, { Type: DesignConstants.pageTitle })) {
      const myTitleComponent = getFirstArrayElementOrNull(parsedData.nonDisplayableControls, { Type: DesignConstants.pageTitle });
      setPageTitle(myTitleComponent.Tran);
    }

    // Tell our dialog sidebar that it has tab icons if we found any in our data
    //If we have only one tab or none, we hide all the tabs
    if (parsedData.tabDefinitions.length < 2) {
      setTabBarVisible(false);
      if (parsedData.nonComponentData["TabType"]) {
        if (parsedData.nonComponentData["TabType"] === "None") {
          // Turn off the tab bar
          setTabBarVisible(false);
        }
      }

    } else {
      setTabBarVisible(true);
    }

    const updatedData: Array<any> = [];

    for (const componentModel of parsedData.displayableControls) {
      for (const controlWithParentModel of parsedData.controlsWithParents) {
        if (controlWithParentModel.Parent && controlWithParentModel.Parent.toLowerCase() === componentModel.PropertyName.toLowerCase()) {
          componentModel.AttachedButton = controlWithParentModel;
        }
      }

      updatedData.push((componentModel.Type === DesignConstants.smartlistcomponent || componentModel.Type === DesignConstants.calendarrulessmartlistcomponent) ?
        mergeObjects(componentModel, everyRequestPostData) : componentModel);

    }

    parsedData.nonDisplayableControls = updatedData;


    // Where ready to start populating our controls.  Reset our control validation flags so no old state left over
    setControlErrorFlags({});

    setDialogInitialised(true);

    setPreviousSelectedBlocks([]);
    setSelectedBlocks([]);
    if (parsedData.displayableControls.length > 0) {
      parsedData.displayableControls[0]["previousSelectedBlocks"] = previousSelectedBlocks;
      parsedData.displayableControls[0]["selectedBlocks"] = selectedBlocks;
    }

    setParsedDialogData(parsedData);
    setInitateDisplayComponents(true);
    setRefreshIndex(s => s + 1);

  }, [
    checkPreviewAvailable,
    changeBlockDialogStatus,
    parseDialogData,
    selectedTabBarIconIndex,
    resolveTheSameLevelActions,
    everyRequestPostData,
    previousSelectedBlocks,
    selectedBlocks,
    setDialogSingleStemActionType,
    setBaseUrl,
    setDateOrTimeDialog,
    setNonEmptyGroupName,
    setDialogInitialised,
    setPreviewImageUrl,
    dialogStateToPropsObservable,
    setControlErrorFlags,
    setCloseButtonDefinition,
    setOkButtonDefinition,
    setPageTitle,
    setTabBarVisible,
    setPreviousSelectedBlocks,
    setSelectedBlocks,
    setParsedDialogData,
    setInitateDisplayComponents,
    setRefreshIndex
  ]);


  const designDialogShowErrorBox = useCallback((_: string, bodyText: string, isSuccess = false) => {
    if (isSuccess) {
      toaster.success(bodyText);
    } else {
      toaster.error(bodyText);
    }
  }, [toaster]);

  const showMessageBox = useCallback((titleText: string, bodyText: string, isSuccess = false) => {
    designDialogShowErrorBox(titleText, bodyText, isSuccess);
  }, [designDialogShowErrorBox]);

  // Data Collection Actions
  const designDialogCollectDialogData = useCallback(() => {
    const postControlsData: any = {};

    for (const key in PropertyCollectionDictionary) {
      if (PropertyCollectionDictionary[key]) {
        const controlValueObj = PropertyCollectionDictionary[key];
        if (controlValueObj && controlValueObj.PropertyName) {
          postControlsData[controlValueObj.PropertyName] = controlValueObj.PropertyValue;
        }
      }
    }

    return postControlsData;
  }, [PropertyCollectionDictionary]);

  // Save Dialog Data Action
  const designDialogSaveDialogData = useCallback(async (extraDataToSave: any = null, _: any = null, urlToSaveTo = "") => {
    let saveUrl = baseUrl;
    if (urlToSaveTo) {
      saveUrl = urlToSaveTo;
    }

    let putData = designDialogCollectDialogData();

    if (extraDataToSave !== null) {
      putData = mergeObjects(extraDataToSave, putData);
    }

    if (putData.PageName === "NamedParameterGroup" && putData.SelectedNameParameter === "") {
      return null;
    }

    if (dialogSingleStemActionType !== "") {
      const consolidatedApi = /\/template\/(\w)*\/content$/.test(saveUrl);
      if (consolidatedApi) {
        putData = {
          CiffAction: {
            ActionType: "Update" + dialogSingleStemActionType,
            ActionPayload: {
              SingleActionObject: putData
            }
          }
        };
      }
    }

    changeBlockDialogStatus(true);

    const data: any = await runSimpleAjaxCommandAsync({
      AjaxType: "PutJson",
      Action: "SaveDialog",
      SingleStemActionType: dialogSingleStemActionType,
      AjaxUrl: resolveAbsolutePath(saveUrl),
      AjaxData: putData
    });

    changeBlockDialogStatus(false);

    if (data.XhrStatus === 204) {
      return null;
    } else {
      if (data.Error === "ValidationError") {
        showMessageBox(data.Title, data.Message);
      }
      return data;
    }
  }, [baseUrl, changeBlockDialogStatus, designDialogCollectDialogData, dialogSingleStemActionType, runSimpleAjaxCommandAsync, showMessageBox]);

  // Preview Actions
  const designDialogPreviewDialog = useCallback(async () => {
    const extraPostData: any = nonEmptyGroupName ? { PageName: nonEmptyGroupName } : {};
    let postData: any = mergeObjects(everyRequestPostData, extraPostData);
    if (parsedDialogData.nonComponentData["PostData"]) {
      postData = mergeObjects(postData, parsedDialogData.nonComponentData["PostData"]);
    }

    changeBlockDialogStatus(true);
    postData["Preview"] = "true";

    try {
      const data = await designDialogSaveDialogData(postData, null, "");
      if (data && data.FieldImages) {
        const fieldImageData = data.FieldImages.split("|");
        const previewImageinfo: any = fieldImageData[0];
        const b64Info = base64ResponseToImageInfoAsHMM(previewImageinfo);

        if (b64Info != null) {
          const imageData = b64Info.imagedata;
          const b64Data = imageData.replace(",", ";base64,");
          setPreviewImageUrl(b64Data);
        }
      }
      changeBlockDialogStatus(false);
    } catch (xhr: any) {
      showMessageBox('Error', xhr.status ? getTranslatedString("CE_PreviewFailedError").replace("%1", xhr.status.toString()) : xhr.toString());
      changeBlockDialogStatus(false);
    }
  }, [changeBlockDialogStatus, designDialogSaveDialogData, everyRequestPostData, nonEmptyGroupName, parsedDialogData.nonComponentData, setPreviewImageUrl, showMessageBox, getTranslatedString]);

  // User Level Warning Dialog Action
  const designDialogShowUserLevelWarningDialog = useCallback((titleText: string, bodyText: string, fieldType = "") => {
    const payload = {
      titleText: titleText,
      bodyText: bodyText,
      bodyIcon: "fa-exclamation-circle",
      iconColor: "danger"
    };
    if (fieldType) {
      const fieldTypeText = fieldType.substring(0, fieldType.indexOf("Button"));
      payload["bodyPlaceholderText1"] = "cirrusUserLevel" + fieldTypeText + "FieldWarningMessage";
    }
    resolveTheSameLevelActions({
      type: ActionTypes.EXECUTE_ShowUserLevelWarningDialogAction,
      payload: payload
    });
  }, [resolveTheSameLevelActions]);

  // Load Initial Dialog Data Action
  const designDialogLoadInitialDialogData = useCallback(async () => {
    let postData = mergeObjects(firstRequestPostData, everyRequestPostData);

    if (dialogSingleStemActionType !== "") {
      const isConsolidatedApi = /\/template\/(\w)*\/content$/.test(baseUrl);
      if (isConsolidatedApi) {
        postData = {
          CiffAction: {
            ActionType: "Get" + dialogSingleStemActionType,
            ActionPayload: {
              SingleActionObject: postData
            }
          }
        };
      }
    }

    try {
      const data: any = await runSimpleAjaxCommandAsync({
        AjaxType: "PostJson",
        Action: "LoadInitalDialogData",
        AjaxUrl: baseUrl,
        AjaxData: postData
      });

      if (data.Error) {
        if (data.Error === "FileError") {
          sendActionToParent({
            type: ActionTypes.UP_ShowErrorEndReturnToMainPageAction,
            payload: data
          });
        }
        if (data.Error === "JobFileError") {
          sendActionToParent({
            type: ActionTypes.UP_ShowErrorAndRefreshPageAction,
            payload: data
          });
        }
        if (data.Error === "UserLevelError") {
          designDialogShowUserLevelWarningDialog(data.Title, data.Message);
          dispatch({ type: ActionTypes.STORE_UpdateIsDialogBlockedAction, payload: false });
          closeDialog();
        }
      } else {
        designDialogProcessComponentsData(data);
      }
    } catch (xhr: any) {
      resolveTheSameLevelActions({
        type: ActionTypes.UP_ShowErrorAction,
        payload: {
          titleText: 'Error',
          bodyText: xhr.status ? getTranslatedString("CE_GenericDialogDataLoadError").replace("%1", xhr.status.toString()) : xhr.toString(),
          bodyIcon: "fa-exclamation-circle",
          iconColor: "danger",
          closeDialog: true
        }
      });
    }
  }, [firstRequestPostData, everyRequestPostData, designDialogShowUserLevelWarningDialog, dialogSingleStemActionType, baseUrl, runSimpleAjaxCommandAsync, sendActionToParent, dispatch, closeDialog, designDialogProcessComponentsData, getTranslatedString]);


  // AI Properties Actions
  const getNewAiProperties = useCallback(async (payload: any) => {
    const templateIdEncoded = encodeUTF8(MemoryStoreLibrary.getString('ciffName'));
    const editorTemplateUrl = `/template/${templateIdEncoded}/content`;
    const aiIdValue = payload.value;

    try {
      const data: any = await runSimpleAjaxCommandAsync({
        AjaxType: 'PostJson',
        Action: 'GetDataBuilderNewItemProps',
        AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
        AjaxData: {
          CiffAction: {
            ActionType: 'GetDataBuilderNewItemProps',
            ActionPayload: {
              SingleActionObject: {
                AiId: aiIdValue,
                AIAquisitionMethod: payload.aIAquisitionMethod || 0,
                SubImage: MemoryStoreLibrary.getString('subImage'),
                FieldName: fieldNameShadowCopy,
              },
            },
          },
        }
      });

      data.aiBlock = {
        displayedText: payload.displayedText,
        value: payload.value,
        isUpdate: payload.isUpdate,
        initialIndex: payload.initialIndex,
      };

      sendActionToChildren({
        type: ActionTypes.DOWN_Show_Ai_Block_Properties,
        payload: data
      });

      changeBlockDialogStatus(false);
    } catch (xhr: any) {
      designDialogShowErrorBox(
        'Error',
        xhr.status ? getTranslatedString('CE_DataBuilderDataReadError').replace('%1', xhr.status.toString()) : xhr.toString()
      );
      changeBlockDialogStatus(false);
    }
  }, [runSimpleAjaxCommandAsync, fieldNameShadowCopy, sendActionToChildren, changeBlockDialogStatus, designDialogShowErrorBox, getTranslatedString]);


  // Dialog Close Action
  const designDialogCloseDialog = useCallback(() => {
    setIsDialogClosing(true);
    setDialogSingleStemActionType(undefined);
    closeDialog();
    setParsedDialogData(s => ({ ...s, nonComponentData: {} }));
  }, [closeDialog, setIsDialogClosing, setDialogSingleStemActionType, setParsedDialogData]);

  // Handle Save Response
  const designDialogHandleSaveResponse = useCallback((_, inputData: any) => {
    if (inputData === null) {
      designDialogCloseDialog();
      return;
    }

    if (inputData !== undefined &&
      inputData["UpdateFileProperties"] && inputData["FileProperties"]) {
      resolveTheSameLevelActions({
        type: ActionTypes.UP_GridPanelSetFileDataAction,
        payload: {
          panelId: "__File__",
          data: inputData["FileProperties"]
        }
      });
      designDialogCloseDialog();
      return;
    }

    if (inputData && inputData["Error"] && inputData.Error === "ValidationError") {
      setSelectedTabIndexInvoker(dispatch, 0);
      return;
    }

    if (inputData && inputData["Error"] && inputData.Error === "UserLevelError") {
      designDialogShowUserLevelWarningDialog(inputData.Title, inputData.Message);
      return;
    }

    designDialogProcessComponentsData(inputData);
  }, [dispatch, designDialogProcessComponentsData, designDialogCloseDialog, resolveTheSameLevelActions, designDialogShowUserLevelWarningDialog]);

  // Refresh Dialog with Stacked Dialog
  const designDialogRefreshDialogWithStackedDialogWithTheSameDialogType = useCallback(async (getTranslatedString) => {
    const lastStackedDialogData = stackedDialogs.current.pop();
    lastStackedDialogData.PostData.CiffAction.ActionPayload.SingleActionObject = mergeObjects(
      lastStackedDialogData.PostData.CiffAction.ActionPayload.SingleActionObject,
      { DialogInError: false }
    );

    try {
      const data: any = await runSimpleAjaxCommandAsync({
        AjaxType: "PostJson",
        Action: "RefreshDialogWithStacked",
        AjaxUrl: resolveAbsolutePath(lastStackedDialogData.DialogUrl),
        AjaxData: lastStackedDialogData.PostData
      });
      data.SingleStemActionType = lastStackedDialogData.DialogSingleStemActionType;
      setBaseUrl(lastStackedDialogData.DialogUrl);
      changeBlockDialogStatus(false);
      designDialogProcessComponentsData(data);
    } catch (xhr: any) {
      showMessageBox('Error', xhr.status ? getTranslatedString("CE_DataBuilderDataReadError").replace("%1", xhr.status.toString()) : xhr.toString());
      changeBlockDialogStatus(false);
    }
  }, [getTranslatedString, stackedDialogs, runSimpleAjaxCommandAsync, changeBlockDialogStatus, designDialogProcessComponentsData, showMessageBox, setBaseUrl, mergeObjects]);

  // Extract Exception Name
  const extractExceptionName = useCallback((message: string): string | null => {
    const regex = /'([^']+)'/;
    const match = message.match(regex);

    if (match && match[1]) {
      const parts = match[1].split('.');
      return parts[parts.length - 1];
    }

    return null;
  }, []);

  // Add New Field
  const designDialogAddNewField = useCallback((getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fileNameInputObj) => {
    sendActionToParent({
      type: ActionTypes.EXECUTE_ShowInputBoxAction,
      payload: {
        alertTitle: getTranslatedString("newFieldNameInput"),
        alertMessage: getTranslatedString("newFieldNameInputMessage"),
        alertIcon: "",
        inputText: fileNameInputObj.inputText,
        iconColor: "info",
        helpMessage: fileNameInputObj.helpMessage,
        helpMessageColor: fileNameInputObj.helpMessageColor,
        thenFunc: async (data: any) => {
          if (data.state === "entered") {
            const enteredFieldName: string = data.input;
            if (enteredFieldName === "") {
              fileNameInputObj.helpMessage = "Enter a valid name";
              fileNameInputObj.helpMessageColor = "danger";
              changeBlockDialogStatus(false);
              designDialogAddNewField(getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fileNameInputObj);
            } else {
              changeBlockDialogStatus(true);

              try {
                const checkData: any = await runSimpleAjaxCommandAsync({
                  AjaxType: "PostJson",
                  Action: "GetFieldNameAvailability",
                  AjaxUrl: resolveAbsolutePath(fileNameInputObj.editorTemplateUrl),
                  AjaxData: {
                    CiffAction: {
                      ActionType: "GetFieldNameAvailability",
                      ActionPayload: {
                        SingleActionObject: {
                          FieldName: enteredFieldName
                        }
                      }
                    }
                  }
                });

                if (!checkData.Available) {
                  fileNameInputObj.helpMessage = getTranslatedString("WarningExistingFieldName");
                  fileNameInputObj.helpMessageColor = "danger";
                  changeBlockDialogStatus(false);
                  designDialogAddNewField(getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fileNameInputObj);
                } else {
                  const addFieldPostData: any = mergeObjects(fileNameInputObj.buttonPayload, {
                    FieldXPos: initialAddFieldXPos,
                    FieldYPos: initialAddFieldYPos,
                    FieldName: data.input,
                    SubImage: fileNameInputObj.subImageId
                  });

                  try {
                    const addFieldReturnData: any = await runSimpleAjaxCommandAsync({
                      AjaxType: "PutJson",
                      Action: "AddField",
                      AjaxUrl: resolveAbsolutePath(fileNameInputObj.editorTemplateUrl),
                      AjaxData: {
                        CiffAction: {
                          ActionType: "AddField",
                          ActionPayload: {
                            SingleActionObject: addFieldPostData
                          }
                        }
                      }
                    });

                    if (addFieldReturnData.Error) {
                      if (addFieldReturnData.Error === "UserLevelError") {
                        designDialogShowUserLevelWarningDialog(addFieldReturnData.Title, addFieldReturnData.Message);
                        changeBlockDialogStatus(false);
                        designDialogCloseDialog();
                      } else {
                        throw { customStatus: addFieldReturnData.Message };
                      }
                    } else {
                      const companyId = isPureDesignUser ? selectedSiteId : selectedCompanyId;
                      const siteId = isPureDesignUser ? selectedSiteId : selectedSiteIdForCompany;

                      configActions.setTrackerActionObject({
                        companyId,
                        printerModel: printerType,
                        printerFormat,
                        plantId: siteId,
                        fieldType: `${addFieldPostData.FieldType}${addFieldPostData.FieldSubTypeBarcode ? `-${addFieldPostData.FieldSubTypeBarcode}` : ""}${addFieldPostData.DateFieldType ? `-${addFieldPostData.DateFieldType}` : ""}`,
                        PathName: "/design/template/add-field"
                      });
                      showMessageBox('Info', _T("Field added successfully."), true);
                    }

                    sendActionToParent({
                      type: ActionTypes.UP_CiffEditorAddNewFieldToEditorCompletedAction,
                      payload: {
                        FieldName: addFieldReturnData.FieldName,
                        FieldImage: addFieldReturnData.FieldImage,
                        FieldProps: addFieldReturnData.FieldProps,
                        AffectedFieldNames: addFieldReturnData.AffectedFieldNames,
                        RedrawImage: addFieldReturnData.RedrawImage,
                        SubImage: addFieldReturnData.SubImage,
                        Width: addFieldReturnData.Width,
                        Height: addFieldReturnData.Height,
                        FileProperties: addFieldReturnData.FileProperties
                      }
                    });

                    changeBlockDialogStatus(false);
                    designDialogCloseDialog();
                  } catch (xhr: any) {
                    const msg = xhr.customStatus || (xhr.status ? getTranslatedString("CE_FieldAdditionError").replace("%1", xhr.status.toString()) : xhr.toString());
                    showMessageBox('Error', msg);
                    changeBlockDialogStatus(false);
                  }
                }
              } catch (xhr: any) {
                fileNameInputObj.inputText = enteredFieldName;
                fileNameInputObj.helpMessage = xhr.statusText;
                fileNameInputObj.helpMessageColor = "danger";
                changeBlockDialogStatus(false);
                designDialogAddNewField(getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fileNameInputObj);
              }
            }
          }
        }
      }
    });
  }, [sendActionToParent,
    changeBlockDialogStatus,
    runSimpleAjaxCommandAsync,
    designDialogCloseDialog,
    designDialogShowUserLevelWarningDialog,
    showMessageBox,
    _T,
    configActions,
    isPureDesignUser,
    selectedSiteId,
    selectedCompanyId,
    printerType,
    printerFormat,
    selectedSiteIdForCompany]);

  // Page Control Validation State
  const designDialogPageControlValidationState = useCallback((validationMessage: any) => {
    if (dialogInitialised) {
      const { PropertyName, State } = validationMessage;
      const temporaryControlErrorFlags = { ...controlErrorFlags };
      temporaryControlErrorFlags[PropertyName] = State === DesignConstants.controlStateValid ? DesignConstants.controlStateValid : DesignConstants.controlStateInvalid;

      let controlStateFound = false;
      for (const key in temporaryControlErrorFlags) {
        if (temporaryControlErrorFlags[key] === DesignConstants.controlStateInvalid) {
          controlStateFound = true;
          break;
        }
      }

      setControlErrorFlags(temporaryControlErrorFlags);

      resolveTheSameLevelActions({
        type: ActionTypes.UP_UpdateShowErrorStatusAction,
        payload: controlStateFound
      });
    }
  }, [controlErrorFlags, dialogInitialised, resolveTheSameLevelActions, setControlErrorFlags]);


  // Refresh Page
  const designDialogRefreshPage = useCallback(async (dispatch, getTranslatedString, refreshData: any) => {
    const refreshObject: any = {};
    refreshObject[refreshData.PropertyName] = refreshData.PropertyValue;
    const pageName = parsedDialogData.tabDefinitions.length === 0
      ? "NoPage"
      : parsedDialogData.tabDefinitions[selectedTabBarIconIndex].Page;
    let postData = mergeObjects(everyRequestPostData, refreshObject, { PageName: pageName });

    if (parsedDialogData.nonComponentData["postData"]) {
      postData = mergeObjects(postData, parsedDialogData.nonComponentData["postData"]);
    }

    postData["UpdateType"] = "Refresh";

    try {
      const data = await designDialogSaveDialogData(postData, null, baseUrl);
      designDialogHandleSaveResponse(dispatch, data);
    } catch (xhr: any) {
      showMessageBox('Error', xhr.status ? getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status.toString()) : xhr.toString());
    }
  }, [baseUrl, everyRequestPostData, designDialogHandleSaveResponse, designDialogSaveDialogData, parsedDialogData.nonComponentData, parsedDialogData.tabDefinitions, selectedTabBarIconIndex, showMessageBox, parsedDialogData]);


  // Collect Composite Data Builder Field Data
  const collectCompositeDataBuilderFieldDataToSendToServer = useCallback((
    _: boolean,
    doubleClickedBlock: any = null
  ) => {
    const editCompositeDataFieldData = designDialogCollectDialogData();

    const propsList = [
      'AIAquisitionMethod',
      'AIUserData',
      'AiTypeString',
      'AiStartIndex',
      'AIAvailableFields',
      'AIRequiredDateFormat',
      'AvailableAis',
    ];

    const selectedAiBlocks = editCompositeDataFieldData.SelectedAiBlocks;
    const allBlocks = selectedAiBlocks && selectedAiBlocks.AllBlocks || [];
    const selectedBlock = doubleClickedBlock || (selectedAiBlocks ? selectedAiBlocks.DoubleClickedBlock : null);

    if (selectedBlock) {
      const updateBlock = allBlocks.find(
        (f: any) => f.InitialIndex === selectedBlock.InitialIndex && f.Value === selectedBlock.Value
      );
      if (updateBlock) {
        const propertiesToChange = updateBlock.PropertiesToChange;
        propsList.forEach((p) => {
          const value = editCompositeDataFieldData[p];
          if (value) {
            const property = propertiesToChange.find((f: any) => f.PropertyName === p);

            if (property) {
              property.PropertyValue = value;
            } else {
              propertiesToChange.push({ PropertyName: p, PropertyValue: value });
            }
          }
        });
      }
    }

    const blockListComponent = getFirstArrayElementOrNull(
      parsedDialogData.displayableControls,
      { PropertyName: 'SelectedAiBlocks' }
    );
    if (blockListComponent) {
      editCompositeDataFieldData['DataBuilderListBlocks'] = blockListComponent.InitialData;
    }

    return editCompositeDataFieldData;
  }, [designDialogCollectDialogData, parsedDialogData.displayableControls]);

  // Get Existing AI Properties
  const getExistingAiProperties = useCallback(async (payload: any) => {
    if (!payload) {
      resolveTheSameLevelActions({ type: ActionTypes.UP_Reset_Ai_Properties_Form });
      return;
    }

    const templateIdEncoded = encodeUTF8(MemoryStoreLibrary.getString('ciffName'));
    const editorTemplateUrl = `/template/${templateIdEncoded}/content`;

    const subImageId = MemoryStoreLibrary.getString('subImage');
    const editDataBuilderFieldData = collectCompositeDataBuilderFieldDataToSendToServer(false);
    editDataBuilderFieldData.SubImage = subImageId;
    editDataBuilderFieldData.FieldName = fieldNameShadowCopy;

    changeBlockDialogStatus(true);

    if (editDataBuilderFieldData.SelectedAiBlocks) {
      editDataBuilderFieldData.SelectedAiBlocks.DoubleClickedBlock = { InitialIndex: payload.initialIndex };
    }

    try {
      const data: any = await runSimpleAjaxCommandAsync({
        AjaxType: 'PostJson',
        Action: 'GetDataBuilderItemProps',
        AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
        AjaxData: {
          CiffAction: {
            ActionType: 'GetDataBuilderItemProps',
            ActionPayload: {
              SingleActionObject: editDataBuilderFieldData,
            },
          },
        }
      });
      data.aiBlock = payload;
      data.aiBlock.isUpdate = true;
      sendActionToChildren({
        type: ActionTypes.DOWN_Show_Ai_Block_Properties,
        payload: data
      });
      changeBlockDialogStatus(false);

    } catch (xhr: any) {
      designDialogShowErrorBox(
        'Error',
        xhr.status ? getTranslatedString('CE_DataBuilderDataReadError').replace('%1', xhr.status.toString()) : xhr.toString()
      );
      changeBlockDialogStatus(false);
    }
  }, [fieldNameShadowCopy, changeBlockDialogStatus, resolveTheSameLevelActions, runSimpleAjaxCommandAsync, sendActionToChildren, designDialogShowErrorBox, getTranslatedString, collectCompositeDataBuilderFieldDataToSendToServer]);


  // Collect Merge Field Data
  const collectMergeFieldDataToSendToServer = useCallback((_: boolean) => {
    if (dialogName === "mergefieldmanager" || dialogName === "compositedatabuildermanager") {
      const editMergeFieldData = designDialogCollectDialogData();

      editMergeFieldData['MergedFieldsListBlocks'] = getFirstArrayElementOrNull(
        parsedDialogData.displayableControls,
        { PropertyName: 'MergedFieldsList' }
      ).InitialData;

      return editMergeFieldData;
    } else {
      return [];
    }
  }, [dialogName, designDialogCollectDialogData, parsedDialogData.displayableControls]);

  // Render Tab
  const designDialogRenderTab = useCallback(() => {
    if (tabBarVisible) {
      if (parsedDialogData.tabDefinitions.length > 0) {
        sendActionToChildren({
          type: ActionTypes.DOWN_SetIconsAction,
          payload: parsedDialogData.tabDefinitions
        });
      } else {
        sendActionToChildren({
          type: ActionTypes.DOWN_ClearIconsAction
        });
      }
    }
  }, [tabBarVisible, parsedDialogData.tabDefinitions, sendActionToChildren]);

  const designDialogHandleButtonClick = useCallback(async (getTranslatedString, buttonData: any, handlingType = "") => {
    if (handlingType === DesignConstants.linkSuffixSave || buttonData.HandlingType === DesignConstants.linkSuffixSave) {

      let postData: any = mergeObjects(everyRequestPostData, buttonData.Result);

      if (parsedDialogData.nonComponentData["PostData"]) { // If we have any extra data passed back from last rest call...
        postData = mergeObjects(postData, parsedDialogData.nonComponentData["PostData"]);
      }

      postData["UpdateType"] = "Save";

      if (dialogSingleStemActionType === 'SubImagesProps') {
        //We need to refresh the current screen
        sendActionToParent({
          type: ActionTypes.UP_RefreshDrawFilePanelAction
        });
        designDialogCloseDialog();
        return;
      }

      changeBlockDialogStatus(true);


      try {
        const data: any = await designDialogSaveDialogData(postData, buttonData);
        if (postData.PageName === "CurrentParametersGroup" || buttonData.PageName === "PackageCodeProperties") {
          sendActionToParent({ type: ActionTypes.UP_FileIconEnableAction, payload: "fileIcon" });
        }
        if (data && data.PostData && data.PostData["FieldName"]) {
          everyRequestPostData.FieldName = data.PostData["FieldName"];
        }

        if (data && data.FieldImages) {
          const fieldImageData = data.FieldImages.split("|");
          //Refresh Editor and Grid in parallel
          fieldImageData.forEach((fieldImageInfo) => {
            sendActionToParent({
              type: ActionTypes.UP_CiffEditorRefreshFieldImageAction,
              payload: { FieldImageInfo: fieldImageInfo }
            });
          });
        }

        if (data && data.AffectedFieldNames) {
          sendActionToParent({
            type: ActionTypes.UP_CiffEditorRefreshAffectedFieldsImageAction,
            payload: { AffectedFieldNames: data.AffectedFieldNames }
          });
        }

        if (data && data.FieldProps) {
          const fieldsNameArray: Array<string> = data.FieldProps.map((fieldInfo: any) => {
            return fieldInfo.Name;
          });

          sendActionToParent({
            type: ActionTypes.UP_ReloadPropertyGridForNamedFieldsAction,
            payload: { FieldsNameArray: fieldsNameArray }
          });
        } else if (data && data.AffectedFieldNames) {
          const fieldsNameArray: Array<string> = data.AffectedFieldNames;

          sendActionToParent({
            type: ActionTypes.UP_ReloadPropertyGridForNamedFieldsAction,
            payload: { FieldsNameArray: fieldsNameArray }
          });
        }

        if (data && data.refreshGrid || buttonData.PageName === "PackageCodeProperties") {
          //We need to refresh the current screen
          sendActionToParent({
            type: ActionTypes.UP_RefreshDrawFilePanelAction
          });
        }

        if (data && data.TierRefreshRequired) {
          resolveTheSameLevelActions({
            type: ActionTypes.UP_RefreshJobTierFilterListAndUpdateListAction,
            payload: data
          });
        }

        if (data && data.Error === "UserLevelError") {
          designDialogShowUserLevelWarningDialog(data.Title, data.Message);
          return;
        } else if (data && data.Error) {
          return;
        }

        if (stackedDialogs.current.length === 0) {
          changeBlockDialogStatus(false);
          if (extraHandlerOnSave) {
            extraHandlerOnSave();
          }
          designDialogCloseDialog();

        } else {
          designDialogRefreshDialogWithStackedDialogWithTheSameDialogType(getTranslatedString);
        }

        // We display toasters for adding, deleting and updating
        if (data) {
          if (postData.PageName === "NamedParameterGroup") {
            showMessageBox('Info', _T("Parameter added successfully."), true);
          } else if ((postData.PageName === "MarkSetParametersGroup" || postData.PageName === "CurrentParametersGroup")) {
            showMessageBox('Info', _T("Parameter updated successfully."), true);
          } else if ((postData.PageName === "UserConcessions" || postData.PageName === "Calculation" || postData.PageName === "Rounding" || postData.PageName === "CalendarRules")) {
            showMessageBox('Info', _T("Date offset updated successfully."), true);
          } else if (postData.PageName === "Printhead") {
            showMessageBox('Info', _T("Printhead updated successfully."), true);
          }
        }


      } catch (xhr: any) {
        showMessageBox('Error', xhr.status ? getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status.toString()) : xhr.toString());
        changeBlockDialogStatus(false);
      }

      return;
    }

    // "/revert" endpoint handler
    if (handlingType === DesignConstants.linkSuffixRevert || buttonData.HandlingType === DesignConstants.linkSuffixRevert) {
      const postData = everyRequestPostData;
      changeBlockDialogStatus(true);
      try {
        const data = await runSimpleAjaxCommandAsync({
          AjaxType: "PostJson",
          Action: "RevertDialog",
          AjaxUrl: buttonData.Link,
          AjaxData: postData
        });

        if (data === null || data === undefined) {
          designDialogCloseDialog();
        } else {
          changeBlockDialogStatus(false);
          designDialogProcessComponentsData(data);
        }
      } catch (xhr: any) {
        designDialogCloseDialog();
        showMessageBox('Error', xhr.status ? getTranslatedString("CE_RevertDataError").replace("%1", xhr.status.toString()) : xhr.toString());
        changeBlockDialogStatus(false);
      }
      return;
    }

    // "/refresh" endpoint handler
    if (handlingType === DesignConstants.linkSuffixRefresh || buttonData.HandlingType === DesignConstants.linkSuffixRefresh) {
      let postData = mergeObjects(everyRequestPostData, buttonData.Result, { PageName: buttonData.PageName });
      if (parsedDialogData.nonComponentData["PostData"]) { // If we have any extra data passed back from last rest call...
        postData = mergeObjects(postData, parsedDialogData.nonComponentData["PostData"]);
      }

      postData["UpdateType"] = "Refresh";

      changeBlockDialogStatus(true);
      try {
        const data: any = await designDialogSaveDialogData(postData, buttonData, buttonData.Link);

        setPageRefreshed(true);

        changeBlockDialogStatus(false);
        if (data && data.Error === "UserLevelError") {
          designDialogShowUserLevelWarningDialog(data.Title, data.Message);
          return;
        } else if (data && data.Error) {
          return;
        }
        designDialogHandleSaveResponse(dispatch, data);

      }
      catch (xhr: any) {
        showMessageBox('Error', xhr.status ? getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status.toString()) : xhr.toString());
        changeBlockDialogStatus(false);
      }
      return;
    }

    // does the button have it's open dialog flag set? (Created due to a "CMD_DIALOG/...." link
    if (buttonData.OpensDialog) {
      if (buttonData.Result["MustSave"]) {
        // First save the possibly yet unsaved information in the generic dialog if correspongind flag is set.
        // Then we can change the dialog.
        changeBlockDialogStatus(true);

        const savePostData = mergeObjects(everyRequestPostData, buttonData.Result);

        savePostData["UpdateType"] = "Save";

        try {
          await designDialogSaveDialogData(savePostData);

          designDialogCloseDialog();
          const postData = mergeObjects(
            everyRequestPostData,
            buttonData.Result
          );

          openDialog({
            initialDataUrl: resolveAbsolutePath(buttonData.Link),
            postData: postData, //Data that's sent with EVERY network/rest request
            controlPageName: "generic",
            firstTimePostData: firstRequestPostData // Data that's sent ONLY with the first network/rest request
          });

          changeBlockDialogStatus(false);
        } catch (xhr: any) {
          designDialogCloseDialog();
          showMessageBox('Error', xhr.status ? getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status.toString()) : xhr.toString());
          changeBlockDialogStatus(false);
        }
      } else {
        // Go on with changing the dialog to a new one, potentially loosing any unsaved information in current dialog.
        designDialogCloseDialog();
        const postData = mergeObjects(everyRequestPostData, buttonData.Result);

        openDialog({
          initialDataUrl: resolveAbsolutePath(buttonData.Link),
          postData: postData, // Data that's sent with EVERY network/rest request
          controlPageName: buttonData.Result["ControlPageName"] ? buttonData.Result.ControlPageName : "generic",
          firstTimePostData: firstRequestPostData  // Data that's sent ONLY with the first network/rest request
        });
      }
    }

    // does the button have it's calls function flag set? (Created due to a "CMD_FUNCTION/...." link
    if (buttonData.CallsFunction) {
      const postData = buttonData.Result;
      const functionUrl = buttonData.Link;

      const designDialogFunctionDataReceived = buttonData.GenericDialogFunctionDataReceived;
      const designDialogFunctionCallFailed = buttonData.GenericDialogFunctionCallFailed;
      changeBlockDialogStatus(true);

      try {
        const data: any = await runSimpleAjaxCommandAsync({
          AjaxType: "Post",
          Action: "RunGenericFunction",
          AjaxUrl: resolveAbsolutePath(functionUrl),
          AjaxData: postData
        });
        const messageData = {
          sentData: postData,
          recievedData: data,
          functionUrl: functionUrl
        };
        if (designDialogFunctionDataReceived) {
          designDialogFunctionDataReceived(messageData);
        }
        changeBlockDialogStatus(false);
      } catch (xhr: any) {
        const messageData = {
          jqXhr: xhr,
          functionUrl: functionUrl
        };

        if (designDialogFunctionCallFailed) {
          designDialogFunctionCallFailed(messageData);
        }

        changeBlockDialogStatus(false);
      }

    }

    // If no link at all.....
    if (!buttonData.Link) {
      designDialogCloseDialog();
      return;
    }
  }, [everyRequestPostData, openDialog, sendActionToParent, parsedDialogData.nonComponentData, dialogSingleStemActionType, changeBlockDialogStatus, resolveTheSameLevelActions, designDialogCloseDialog, designDialogSaveDialogData, stackedDialogs, designDialogShowUserLevelWarningDialog, extraHandlerOnSave, designDialogRefreshDialogWithStackedDialogWithTheSameDialogType, showMessageBox, runSimpleAjaxCommandAsync, designDialogProcessComponentsData, designDialogHandleSaveResponse, dispatch, firstRequestPostData, _T, extraHandlerOnCancel]);

  const executeButtonLink = useCallback(async (linkRequestObj: any) => {
    if (dialogName === "designDialog") {
      // Do nothing
    } else if (dialogName === "mergefieldmanager" || dialogName === "compositedatabuildermanager") {

      //If this is a real button, call handler directly
      if (linkRequestObj && linkRequestObj.Result) {
        designDialogHandleButtonClick(getTranslatedString, linkRequestObj);
      } else {
        const buttonLink: string = linkRequestObj.Link;
        const buttonArgs = linkRequestObj.Args;
        const templateIdEncoded = encodeUTF8(MemoryStoreLibrary.getString("ciffName"));
        let editorTemplateUrl = `/template/${templateIdEncoded}/content`;
        const subImageId = MemoryStoreLibrary.getString("subImage");

        switch (buttonLink) {
          case "EditMergeItem":
            {
              //We should not do anything if we are already inside the merge field edit
              if (buttonArgs && buttonArgs.Page && buttonArgs.Page === 'MergeFieldEditField') {
                return;
              }

              const editMergeFieldData: any = collectMergeFieldDataToSendToServer(false);
              editMergeFieldData.SubImage = subImageId;
              editMergeFieldData.FieldName = fieldNameShadowCopy;

              changeBlockDialogStatus(true);

              // return;

              try {

                const data: any = await runSimpleAjaxCommandAsync({
                  AjaxType: "PutJson",
                  Action: "SaveMerge",
                  AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                  AjaxData: {
                    CiffAction: {
                      ActionType: "UpdateMergeProps",
                      ActionPayload: {
                        SingleActionObject: editMergeFieldData
                      }
                    }
                  }
                });
                if (data.Error === "ValidationError") {
                  designDialogShowErrorBox(data.Title, data.Message);
                } else {

                  const fieldId = data.FieldProps[0].Name; //data.FieldName;

                  editMergeFieldData.FieldName = fieldId;

                  //Now Open EditMergeItem
                  changeBlockDialogStatus(true);

                  //Add the previous dialog to the dialog stack
                  stackedDialogs.current.push({
                    DialogUrl: resolveAbsolutePath(editorTemplateUrl),
                    DialogControlName: "mergefieldmanager",
                    PostData: {
                      CiffAction: {
                        ActionType: "GetMergeProps",
                        ActionPayload: {
                          SingleActionObject: {
                            SubImage: subImageId,
                            FieldName: fieldId
                          }
                        }
                      }
                    },
                    DialogSingleStemActionType: "MergeProps",
                  });

                  try {
                    const data: any = await runSimpleAjaxCommandAsync({
                      AjaxType: "PostJson",
                      Action: "UpdateMergeItemProps",
                      AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                      AjaxData: {
                        CiffAction: {
                          ActionType: "GetMergeItemProps",
                          ActionPayload: {
                            SingleActionObject: editMergeFieldData
                          }
                        }
                      }
                    });
                    data.SingleStemActionType = "MergeItemProps";
                    setBaseUrl(editorTemplateUrl);
                    designDialogProcessComponentsData(data);
                    changeBlockDialogStatus(false);

                  } catch (xhr: any) {
                    designDialogShowErrorBox('Error', xhr.status ? getTranslatedString("CE_DataBuilderDataReadError").replace("%1", xhr.status.toString()) : xhr.toString());
                    changeBlockDialogStatus(false);
                  }
                }

              } catch (xhr: any) {
                designDialogCloseDialog();
                designDialogShowErrorBox('Error', xhr.status ? getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status.toString()) : xhr.toString());
                changeBlockDialogStatus(false);
              }

            }
            break;
          case 'EditDataBuilderItem': {
            if (buttonArgs && buttonArgs.Page && buttonArgs.Page === 'SetupBlocks') {
              return;
            }

            const doubleClickedBlock = linkRequestObj.isUpdate
              ? { InitialIndex: linkRequestObj.initialIndex, Value: linkRequestObj.value }
              : null;
            const editDataBuilderFieldData = collectCompositeDataBuilderFieldDataToSendToServer(
              false,
              doubleClickedBlock
            );
            editDataBuilderFieldData.SubImage = subImageId;
            editDataBuilderFieldData.FieldName = fieldNameShadowCopy;

            changeBlockDialogStatus(true);

            editorTemplateUrl = `/template/${templateIdEncoded}/content`;
            try {
              const data: any = await runSimpleAjaxCommandAsync({
                AjaxType: 'PutJson',
                Action: 'SaveDataBuilder',
                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                AjaxData: {
                  CiffAction: {
                    ActionType: 'UpdateDataBuilderListProps',
                    ActionPayload: {
                      SingleActionObject: editDataBuilderFieldData,
                    },
                  },
                }
              });
              if (data.Error === 'ValidationError') {
                changeBlockDialogStatus(false);
                designDialogShowErrorBox(data.Title, data.Message);
                designDialogLoadInitialDialogData();
              } else {
                resolveTheSameLevelActions({ type: ActionTypes.UP_Reset_Ai_Properties_Form });
                resolveTheSameLevelActions({
                  type: ActionTypes.UP_ReloadPropertyGridForNamedFieldsAction,
                  payload: { FieldsNameArray: [fieldNameShadowCopy] },
                });

                if (data && data.FieldImages) {
                  const fieldImageData = data.FieldImages.split('|');

                  fieldImageData.forEach((fieldImageInfo: any) => {
                    resolveTheSameLevelActions({
                      type: ActionTypes.UP_CiffEditorRefreshFieldImageAction,
                      payload: { FieldImageInfo: fieldImageInfo },
                    });
                  });
                }
                designDialogLoadInitialDialogData();
              }

            } catch (xhr: any) {
              designDialogCloseDialog();
              designDialogShowErrorBox(
                'Error',
                xhr.status ? getTranslatedString('CE_SaveErrorWithCode').replace('%1', xhr.status.toString()) : xhr.toString()
              );
              changeBlockDialogStatus(false);
            }
            break;
          }
        }
      }
    }
  }, [dialogName, designDialogHandleButtonClick, getTranslatedString, collectMergeFieldDataToSendToServer, changeBlockDialogStatus, fieldNameShadowCopy, runSimpleAjaxCommandAsync, designDialogShowErrorBox, designDialogProcessComponentsData, designDialogCloseDialog, collectCompositeDataBuilderFieldDataToSendToServer, designDialogLoadInitialDialogData, resolveTheSameLevelActions, stackedDialogs]);


  // Preview Dialog
  const previewDialog = useCallback(() => {
    if (dialogName === 'mergefieldmanager') {
      if (previewAvailable) {
        designDialogPreviewDialog();
      }
    }
    else if (dialogName !== 'compositedatabuildermanager') {
      designDialogPreviewDialog();
    } else {
      if (aiBlocksReordered) {
        executeButtonLink({ Link: 'EditDataBuilderItem' });
        setAiBlocksReordered(false);
      } else {
        const data = designDialogCollectDialogData();
        const canPreviewAvailable = data && data.SelectedAiBlocks && data.SelectedAiBlocks.AllBlocks && data.SelectedAiBlocks.AllBlocks.length !== 0;
        if (canPreviewAvailable) {
          designDialogPreviewDialog();
        }
      }
    }
  }, [executeButtonLink, aiBlocksReordered, designDialogPreviewDialog, previewAvailable, dialogName, designDialogCollectDialogData, setAiBlocksReordered]);

  const actions: IDesignDialogActions = {
    // Dialog Management
    designDialogCloseDialog,
    designDialogHandleSaveResponse,
    designDialogRefreshDialogWithStackedDialogWithTheSameDialogType,

    // Field Management
    designDialogAddNewField,
    extractExceptionName,

    // Validation and UI
    designDialogPageControlValidationState,
    designDialogRefreshPage,
    designDialogRenderTab,

    // AI and Data Builder
    getExistingAiProperties,
    collectMergeFieldDataToSendToServer,

    // Preview
    previewDialog,
    executeButtonLink,
    designDialogHandleButtonClick,

    // Basic Actions
    updateDialogError,
    updateDialogBlocked,
    sendActionToChildren,
    changeBlockDialogStatus,

    // Error Handling
    designDialogShowErrorBox,
    showMessageBox,
    designDialogShowUserLevelWarningDialog,

    // Data Management
    designDialogCollectDialogData,
    designDialogSaveDialogData,
    designDialogLoadInitialDialogData,

    // Preview
    designDialogPreviewDialog,

    // AI Properties
    getNewAiProperties,

    // Action Resolution
    resolveTheSameLevelActions,

    pageTitle,
    setPageTitle,
    dialogOpenIndex,
    setDialogOpenIndex,
    isDialogOpen,
    setIsDialogOpen,
    dialogName,
    setDialogName,
    isDialogVisible,
    setIsDialogVisible,
    refreshIndex,
    setRefreshIndex,
    baseUrl,
    setBaseUrl,
    maxWidth,

    // Dialog Lifecycle States
    initateDisplayComponents,
    setInitateDisplayComponents,
    isDialogClosing,
    setIsDialogClosing,
    dialogInitialised,
    setDialogInitialised,
    pageRefreshed,
    setPageRefreshed,
    previousActionFromParent,
    setPreviousActionFromParent,

    // Data Management States
    PropertyCollectionDictionary,
    updatePropertyCollectionDictionary,
    everyRequestPostData,
    firstRequestPostData,
    fieldNameShadowCopy,
    setFieldNameShadowCopy,
    parsedDialogData,
    setParsedDialogData,

    // UI Control States
    tabBarVisible,
    setTabBarVisible,
    okButtonDefinition,
    setOkButtonDefinition,
    closeButtonDefinition,
    setCloseButtonDefinition,
    selectedTabBarIconIndex,
    setSelectedTabBarIconIndex,
    subAddFieldDialogOpened,
    setSubAddFieldDialogOpened,

    // Validation and Error States
    controlErrorFlags,
    setControlErrorFlags,
    isDialogInError,
    setIsDialogInError,

    // Dialog Type Specific States
    nonEmptyGroupName,
    setNonEmptyGroupName,
    setDateOrTimeDialog,
    dialogSingleStemActionType,
    setDialogSingleStemActionType,

    // Preview Related States
    previewImageUrl,
    setPreviewImageUrl,
    previewWorkflowIndex,
    setPreviewWorkflowIndex,

    // Block Management States
    aiBlocksReordered,
    setAiBlocksReordered,
    previousSelectedBlocks,
    setPreviousSelectedBlocks,
    selectedBlocks,
    setSelectedBlocks,

    // User Input States
    isUserEditable,
    setIsUserEditable,
    isFixedLength,
    setIsFixedLength,

    // Warning/Message States
    defaultUserLevelWarningTitle,
    defaultUserLevelWarningMessage,

    // Refs
    dialogStateToPropsObservable,
    stackedDialogs,

    // Computed States
    mergeFieldDialog,
    dialogTabBarProps,
    previewAvailableInDialog,

    // Helper properties from dialogProps
    extraHandlerOnCancel,
    extraHandlerOnSave,

    // Utility Functions
    checkPreviewAvailable,
    closeDialog,
    openDialog,
    previewAvailable,
    designDialogProcessComponentsData,

    runSimpleAjaxCommandAsync,
    sendActionToParent,
  };

  // The provider then makes the fresh `actions` object available to all children.
  return (
    <DesignDialogActionsContext.Provider value={actions}>
      {children}
    </DesignDialogActionsContext.Provider>
  );
};
